import { Controller } from "@hotwired/stimulus"

/**
 * Builds a SkipIfEvaluator predicate via a structured form, then
 * serializes it into the `edit[when]` text field as a raw DSL string.
 *
 * Why structured-form-only writes the textarea on user action: the
 * textarea is the source of truth for the predicate. Existing
 * predicates load from the saved config into the textarea unchanged,
 * so on `connect()` we MUST NOT auto-serialize an empty builder over a
 * non-empty textarea. The serialize() call only runs in response to
 * `addRow`, `removeRow`, or a row-input change.
 *
 * Supported grammar (subset of `RawImport::SkipIfEvaluator`):
 *
 *   <expr>     := <clause> ( ("and" | "or") <clause> )*
 *   <clause>   := <column> <op> [<value>]
 *   <column>   := identifier | "source header"
 *   <op>       := "==" | "!=" | "is_blank" | "is_present" | "in_list" | "contains_token" | numeric comparison
 *   <value>    := "string" | NUMBER | true | false | nil
 *
 * Mixed and/or, parens, and `not` are NOT supported here — operators
 * who need them edit the raw textarea directly. The builder's job is
 * to remove the magic-syntax cliff for the 95% case.
 *
 * Why event delegation over `data-action` on cloned rows: Stimulus
 * registers actions on elements via a MutationObserver microtask.
 * Tests (and the user's first dispatched event after addRow) can race
 * the observer in jsdom and even in the browser when actions fire
 * within the same tick as the appendChild. One delegated listener on
 * the controller element handles every interior interaction reliably.
 */
const OPS_NEED_VALUE = new Set([
  "==",
  "!=",
  "in_list",
  "contains_token",
  "numeric_gt",
  "numeric_gte",
  "numeric_eq",
  "numeric_lt",
  "numeric_lte",
])

export default class extends Controller {
  static targets = ["joiner", "rows", "output", "rowTemplate", "preview", "matchCount", "matchTotal", "matchPercent"]
  static values = { sampleRows: { type: Array, default: [] as Array<Record<string, unknown>> } }

  declare readonly joinerTarget: HTMLSelectElement
  declare readonly rowsTarget: HTMLElement
  declare readonly outputTarget: HTMLInputElement | HTMLTextAreaElement
  declare readonly rowTemplateTarget: HTMLTemplateElement
  declare readonly hasPreviewTarget: boolean
  declare readonly previewTarget: HTMLElement
  declare readonly hasMatchCountTarget: boolean
  declare readonly matchCountTarget: HTMLElement
  declare readonly hasMatchTotalTarget: boolean
  declare readonly matchTotalTarget: HTMLElement
  declare readonly hasMatchPercentTarget: boolean
  declare readonly matchPercentTarget: HTMLElement
  declare readonly sampleRowsValue: Array<Record<string, unknown>>

  connect(): void {
    this.element.addEventListener("change", this.onChange)
    this.element.addEventListener("input", this.onInput)
    this.element.addEventListener("click", this.onClick)
    this.toggleValueVisibility()
    this.updatePreview()
  }

  disconnect(): void {
    this.element.removeEventListener("change", this.onChange)
    this.element.removeEventListener("input", this.onInput)
    this.element.removeEventListener("click", this.onClick)
  }

  // Public actions — addRow stays callable from explicit `data-action`
  // bindings on static buttons that exist on connect, but the click
  // delegation also routes to it.
  addRow(event?: Event): void {
    event?.preventDefault()
    const fragment = this.rowTemplateTarget.content.cloneNode(true) as DocumentFragment
    this.rowsTarget.appendChild(fragment)
    this.toggleValueVisibility()
    this.serialize()
  }

  removeRow(event: Event): void {
    event.preventDefault()
    const trigger = event.currentTarget as HTMLElement | null
    const row = trigger?.closest("[data-when-builder-target='clauseRow']") as HTMLElement | null
    row?.remove()
    this.serialize()
  }

  changed(): void {
    this.toggleValueVisibility()
    this.serialize()
  }

  // Delegated handlers — fire regardless of whether the originating
  // element existed at connect time.
  private onChange = (event: Event): void => {
    const target = event.target as HTMLElement
    if (this.isRoleMatch(target, "column") || this.isRoleMatch(target, "op")) {
      this.toggleValueVisibility()
      this.serialize()
      return
    }
    if (target === this.joinerTarget) {
      this.serialize()
    }
  }

  private onInput = (event: Event): void => {
    const target = event.target as HTMLElement
    if (this.isRoleMatch(target, "value")) {
      this.serialize()
    }
  }

  private onClick = (event: Event): void => {
    const target = event.target as HTMLElement
    const removeBtn = target.closest("[data-when-builder-role='remove']") as HTMLElement | null
    if (removeBtn) {
      event.preventDefault()
      const row = removeBtn.closest("[data-when-builder-target='clauseRow']") as HTMLElement | null
      row?.remove()
      this.serialize()
    }
  }

  private isRoleMatch(el: HTMLElement, role: string): boolean {
    return el.dataset.role === role
  }

  // Walk the DOM directly rather than relying on `clauseRowTargets`. Stimulus
  // registers targets on a MutationObserver microtask, which can race the
  // first event after `addRow`.
  private get clauseRows(): HTMLElement[] {
    return Array.from(
      this.rowsTarget.querySelectorAll<HTMLElement>(
        "[data-when-builder-target='clauseRow']"
      )
    )
  }

  private toggleValueVisibility(): void {
    for (const row of this.clauseRows) {
      const op = row.querySelector<HTMLSelectElement>("select[data-role='op']")
      const value = row.querySelector<HTMLElement>("[data-role='value-cell']")
      if (op && value) {
        value.classList.toggle("hidden", !OPS_NEED_VALUE.has(op.value))
      }
    }
  }

  private serialize(): void {
    const joiner = this.joinerTarget.value || "and"
    const clauses: string[] = []
    for (const row of this.clauseRows) {
      const column = row.querySelector<HTMLSelectElement>("select[data-role='column']")?.value || ""
      const op = row.querySelector<HTMLSelectElement>("select[data-role='op']")?.value || ""
      const value = row.querySelector<HTMLInputElement>("input[data-role='value']")?.value || ""
      if (!column || !op) continue
      if (OPS_NEED_VALUE.has(op) && !value.trim()) continue
      clauses.push(this.formatClause(column, op, value))
    }
    if (clauses.length === 0) {
      this.outputTarget.value = ""
      this.updatePreview()
      return
    }
    this.outputTarget.value = clauses.join(` ${joiner} `)
    this.updatePreview()
  }

  private updatePreview(): void {
    if (this.hasPreviewTarget) {
      this.previewTarget.textContent = this.outputTarget.value
    }
    this.updateMatchCount()
  }

  // Evaluate the current structured rows against the file's sample rows
  // (passed in via `data-when-builder-sample-rows-value`) and update the
  // match-count panel. Keeps the math entirely client-side — the runtime
  // remains the source of truth on execution; this is a hint, not a gate.
  private updateMatchCount(): void {
    if (!this.hasMatchCountTarget && !this.hasMatchTotalTarget) return

    const rows = Array.isArray(this.sampleRowsValue) ? this.sampleRowsValue : []
    const total = rows.length
    const clauses = this.structuredClauses()

    let matched = 0
    if (clauses.length === 0) {
      matched = total
    } else {
      const joiner = (this.joinerTarget.value || "and").toLowerCase()
      for (const row of rows) {
        const results = clauses.map((c) => this.evaluateClause(row, c))
        const passed = joiner === "or" ? results.some(Boolean) : results.every(Boolean)
        if (passed) matched += 1
      }
    }

    if (this.hasMatchCountTarget) this.matchCountTarget.textContent = matched.toLocaleString()
    if (this.hasMatchTotalTarget) this.matchTotalTarget.textContent = total.toLocaleString()
    if (this.hasMatchPercentTarget) {
      const pct = total === 0 ? 0 : Math.round((matched / total) * 100)
      this.matchPercentTarget.textContent = `${pct}%`
      this.matchPercentTarget.style.setProperty("--iw-match-percent", `${pct}%`)
    }
  }

  private structuredClauses(): Array<{ column: string; op: string; value: string }> {
    const clauses: Array<{ column: string; op: string; value: string }> = []
    for (const row of this.clauseRows) {
      const column = row.querySelector<HTMLSelectElement>("select[data-role='column']")?.value || ""
      const op = row.querySelector<HTMLSelectElement>("select[data-role='op']")?.value || ""
      const value = row.querySelector<HTMLInputElement>("input[data-role='value']")?.value || ""
      if (!column || !op) continue
      if (OPS_NEED_VALUE.has(op) && !value.trim()) continue
      clauses.push({ column, op, value })
    }
    return clauses
  }

  private evaluateClause(row: Record<string, unknown>, clause: { column: string; op: string; value: string }): boolean {
    const cell = row[clause.column]
    const cellStr = cell === null || cell === undefined ? "" : String(cell)

    switch (clause.op) {
      case "is_blank":
        return cellStr.trim() === ""
      case "is_present":
        return cellStr.trim() !== ""
      case "==":
        return cellStr === clause.value
      case "!=":
        return cellStr !== clause.value
      case "in_list": {
        const tokens = clause.value.split(",").map((s) => s.trim()).filter(Boolean)
        return tokens.includes(cellStr)
      }
      case "contains_token": {
        const expected = clause.value.trim().toLocaleLowerCase()
        const tokens = cellStr.split(/[|,;\r\n]+/).map((s) => s.trim().toLocaleLowerCase()).filter(Boolean)
        return tokens.includes(expected)
      }
      case "numeric_gt":
      case "numeric_gte":
      case "numeric_eq":
      case "numeric_lt":
      case "numeric_lte": {
        const left = Number(cellStr)
        const right = Number(clause.value)
        if (Number.isNaN(left) || Number.isNaN(right)) return false
        switch (clause.op) {
          case "numeric_gt": return left > right
          case "numeric_gte": return left >= right
          case "numeric_eq": return left === right
          case "numeric_lt": return left < right
          case "numeric_lte": return left <= right
        }
        return false
      }
      default:
        return false
    }
  }

  private formatClause(column: string, op: string, value: string): string {
    if (op === "is_blank" || op === "is_present") {
      return `${this.formatColumn(column)} ${op}`
    }
    if (op === "in_list") {
      const tokens = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
      return `${this.formatColumn(column)} in_list [${tokens.map(this.formatLiteral).join(", ")}]`
    }
    return `${this.formatColumn(column)} ${op} ${this.formatLiteral(value)}`
  }

  private formatColumn(raw: string): string {
    const trimmed = raw.trim()
    if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(trimmed)) return trimmed
    return this.formatLiteral(trimmed)
  }

  private formatLiteral(raw: string): string {
    const trimmed = raw.trim()
    if (trimmed === "true" || trimmed === "false" || trimmed === "nil") return trimmed
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed
    return `"${trimmed.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`
  }
}
